import { ChangeEvent, KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import styled from "@emotion/styled";
import { InputStyle, ValidationErrorStyle } from "src/common";
import { errorA11yProps, errorIdFor } from "src/common/style/a11y.style";
import { InputLabel } from "src/components";
import { colors } from "src/theme";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const OptionsList = styled.ul({
  margin: 0,
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: colors.background7,
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 99,
});

const OptionItem = styled.li((props: { isActive: boolean }) => ({
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
  listStyle: 'none',
  backgroundColor: props.isActive ? colors.background6 : 'transparent',
  outline: props.isActive ? `2px solid ${colors.primary}` : 'none',
  outlineOffset: '-2px',

  '&:hover': {
    backgroundColor: colors.background6,
  }
}))

interface SelectOption {
  value: string;
  label: string;
  contains?: string;
}

interface SelectProps {
  id?: string;
  label?: string;
  value?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
}

export const Select: React.FC<SelectProps> = ({
  id,
  label,
  value,
  error,
  disabled,
  placeholder,
  options,
  onChange
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  // Index in filteredOptions highlighted by the keyboard, -1 when none
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  const generatedId = useId();
  const inputId = id || generatedId;
  const listboxId = `${inputId}-listbox`;
  const optionId = (index: number) => `${inputId}-option-${index}`;

  // Options load asynchronously, keep the list in sync until the user starts filtering
  useEffect(() => {
    if (!showOptions) setFilteredOptions(options);
  }, [options]);

  // Keep the highlighted option scrolled into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLElement>(`#${CSS.escape(optionId(activeIndex))}`);
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (value) {
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      }
    }
  }, [value, options]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowOptions(true);

    // Filtriranje opcija prema unosu
    const filtered = filterOptions(newValue);
    setFilteredOptions(filtered);
    setActiveIndex(filtered.length > 0 ? 0 : -1);
  };

  const filterOptions = (query: string) => options.filter(opt =>
    opt.label.toLowerCase().includes(query.toLowerCase()) ||
    (opt.contains && opt.contains.toLowerCase().includes(query.toLowerCase()))
  );

  // Shows the whole list when the field holds the current selection, otherwise what matches the typed text
  const openOptions = () => {
    const selectedLabel = options.find(opt => opt.value === value)?.label;
    const filtered = !inputValue || inputValue === selectedLabel ? options : filterOptions(inputValue);
    setFilteredOptions(filtered);
    setShowOptions(true);
  };

  const handleOptionClick = (option: SelectOption) => {
    setInputValue(option.label);
    setShowOptions(false);
    setActiveIndex(-1);
    if (onChange) {
      onChange(option.value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const count = filteredOptions.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!showOptions) openOptions();
        if (count > 0) setActiveIndex(prev => (prev + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!showOptions) openOptions();
        if (count > 0) setActiveIndex(prev => (prev <= 0 ? count - 1 : prev - 1));
        break;
      case 'Home':
        if (showOptions && count > 0) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (showOptions && count > 0) {
          e.preventDefault();
          setActiveIndex(count - 1);
        }
        break;
      case 'Enter':
        if (showOptions && activeIndex >= 0 && activeIndex < count) {
          e.preventDefault();
          handleOptionClick(filteredOptions[activeIndex]);
        }
        break;
      case 'Escape':
        if (showOptions) {
          e.preventDefault();
          setShowOptions(false);
          setActiveIndex(-1);
        }
        break;
      case 'Tab':
        setShowOptions(false);
        setActiveIndex(-1);
        break;
    }
  };

  const isExpanded = showOptions && filteredOptions.length > 0;

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={inputId} disabled={disabled}>{label}</InputLabel> : null}
        <InputStyle
          id={inputId}
          type='text'
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-activedescendant={isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          {...errorA11yProps(inputId, error)}
          value={inputValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={openOptions}
          onClick={() => { if (!showOptions) openOptions(); }}
          onBlur={() => {
            setShowOptions(false);
            setActiveIndex(-1);
          }}
          hasError={!!error}
          autoComplete="off"
        />
        {error ? <ValidationErrorStyle id={errorIdFor(inputId)}>{error}</ValidationErrorStyle> : null}
      </div>
      {isExpanded && (
        <OptionsList id={listboxId} role="listbox" ref={listRef} aria-label={label}>
          {filteredOptions.map((option, index) => (
            <OptionItem 
              key={option.value} 
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              isActive={index === activeIndex}
              // Keep focus in the input so blur does not close the list before the click lands
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </Box>
  );
};